import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Header,
  Modal,
  Multiselect,
  SpaceBetween,
} from "@amzn/awsui-components-react/polaris";

/**
 * Per-deliverable "Row Settings" dialog opened from the admin gear in the
 * Deliverable column. Persists per-service settings to
 * task_manager.service_attributes (via the parent's onSave -> manageServiceAttributes):
 *   - Options: Show Self Certify (ShowSelfCertify)
 *   - Mandatory file types (MandatorySupportingDocuments) - required supporting
 *     doc types the uploader must attach; read back on the FRISCO upload page.
 *   - Notifications: digest / duplicate-upload opt-outs
 *
 * Seeds current values from row.serviceAttributes (the raw
 * task_manager.service_attributes map, keyed by attribute_type). onSave hands the
 * assembled settings to the caller, which owns the persistence + service ids.
 */

// The mandatory supporting-document file types an admin can require. Mirrors the
// SEC Central DeliverablesPage options and the FRISCO upload validation.
const FILE_TYPE_OPTIONS = [
  { label: "PDF", value: "PDF" },
  { label: "Excel", value: "Excel" },
  { label: "Word", value: "Word" },
  { label: "CSV", value: "CSV" },
];

// attribute_type keys in task_manager.service_attributes.
const ATTR = {
  selfCertify: "ShowSelfCertify",
  excludeDigest: "ExcludeFromDigest",
  suppressDuplicate: "SuppressDuplicateNotify",
  mandatoryDocs: "MandatorySupportingDocuments",
};

const isTrue = (v) => v === true || String(v ?? "").trim().toLowerCase() === "true";

const RowSettingsModal = ({ visible, row, onDismiss, onSave, saving = false, error = "" }) => {
  const [showSelfCertify, setShowSelfCertify] = useState(false);
  const [excludeFromDigest, setExcludeFromDigest] = useState(false);
  const [suppressDuplicateNotify, setSuppressDuplicateNotify] = useState(false);
  const [mandatoryDocs, setMandatoryDocs] = useState([]); // [{label,value}]

  // Seed the form from the row's persisted service attributes each open.
  useEffect(() => {
    if (!visible) return;
    const sa = row?.serviceAttributes || {};
    setShowSelfCertify(isTrue(sa[ATTR.selfCertify]));
    setExcludeFromDigest(isTrue(sa[ATTR.excludeDigest]));
    setSuppressDuplicateNotify(isTrue(sa[ATTR.suppressDuplicate]));
    const stored = String(sa[ATTR.mandatoryDocs] || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setMandatoryDocs(FILE_TYPE_OPTIONS.filter((o) => stored.includes(o.value)));
  }, [visible, row]);

  const deliverableName = row?.deliverable || "";

  const handleSave = () => {
    onSave?.({
      showSelfCertify,
      excludeFromDigest,
      suppressDuplicateNotify,
      // Comma-joined canonical values (e.g. "PDF,Excel"), matching the app-level
      // MandatorySupportingDocuments shape the FRISCO upload page reads.
      mandatoryDocs: mandatoryDocs.map((o) => o.value).join(","),
    });
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`Row Settings — ${deliverableName}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header="Failed to save row settings">
            {error}
          </Alert>
        )}
        <SpaceBetween size="s">
          <Header variant="h3">Options</Header>
          <Checkbox
            checked={showSelfCertify}
            onChange={({ detail }) => setShowSelfCertify(detail.checked)}
          >
            Show Self Certify
          </Checkbox>
          <FormField
            label="Mandatory file types"
            description="Supporting document types the uploader must attach on upload."
          >
            <Multiselect
              selectedOptions={mandatoryDocs}
              options={FILE_TYPE_OPTIONS}
              onChange={({ detail }) => setMandatoryDocs(detail.selectedOptions)}
              placeholder="Select required file types"
            />
          </FormField>
        </SpaceBetween>

        <SpaceBetween size="s">
          <Header variant="h3">Reset</Header>
          <Box>
            <Button iconName="refresh">Reset Statuses</Button>
          </Box>
        </SpaceBetween>

        <SpaceBetween size="s">
          <Header variant="h3">Notifications</Header>
          <Checkbox
            checked={excludeFromDigest}
            onChange={({ detail }) => setExcludeFromDigest(detail.checked)}
          >
            Do not include in daily digest
          </Checkbox>
          <Checkbox
            checked={suppressDuplicateNotify}
            onChange={({ detail }) => setSuppressDuplicateNotify(detail.checked)}
          >
            Do not notify duplicate uploads
          </Checkbox>
        </SpaceBetween>
      </SpaceBetween>
    </Modal>
  );
};

export default RowSettingsModal;
